import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getDataProvider } from '@/lib/dataProvider'
import { useAuthStore } from './auth'
import { useAudioCacheStore } from './audioCache'
import { useImageCacheStore } from './imageCache'
import { logger } from '@/lib/logger'
import * as musicMetadata from 'music-metadata'

// TypeScript interfaces based on the database schema
export interface MusicNode {
  id: string
  user_id: string
  name: string
  storage_path: string
  file_name: string
  file_size: number | null
  duration: number | null
  volume: number
  loop: boolean
  playback_target: string | null // 'here' for editor, display UUID, or null (defaults to first display)
  position_x: number
  position_y: number
  created_at: string
  updated_at: string
  container_id: string | null
  playlist_id: string | null // Reference to playlist this node belongs to
  playlist_order: number // Order within the playlist (0-indexed)
  cover_storage_path: string | null // Path to extracted album cover image
  // Runtime properties - not stored in DB
  url?: string
  coverUrl?: string
  isUploading?: boolean // True while file is being uploaded
  uploadError?: string // Set if upload failed
}

export interface CreateMusicNodeData {
  name: string
  file: File
  position_x?: number
  position_y?: number
}

export interface UpdateMusicNodeData {
  name?: string
  volume?: number
  loop?: boolean
  playback_target?: string | null
  position_x?: number
  position_y?: number
  container_id?: string | null
  playlist_id?: string | null
  playlist_order?: number
}

export const useMusicNodesStore = defineStore('musicNodes', () => {
  // State
  const musicNodes = ref<Map<string, MusicNode>>(new Map())
  const loadingStates = ref<Map<string, boolean>>(new Map())
  const error = ref<string | null>(null)
  const isLoading = ref(false)

  // Getters
  const allMusicNodes = computed(() => {
    return Array.from(musicNodes.value.values())
      .sort((a, b) => a.name.localeCompare(b.name))
  })

  const getMusicNodeById = computed(() => (id: string) => {
    return musicNodes.value.get(id)
  })

  const getMusicNodesByPlaylistId = computed(() => (playlistId: string) => {
    return Array.from(musicNodes.value.values())
      .filter(node => node.playlist_id === playlistId)
      .sort((a, b) => a.playlist_order - b.playlist_order)
  })

  const isMusicNodeLoading = computed(() => (id: string) => {
    return loadingStates.value.get(id) || false
  })

  // Get cached audio URL (uses audioCache store for blob caching)
  const getAudioUrl = async (storagePath: string): Promise<string> => {
    const audioCacheStore = useAudioCacheStore()
    return await audioCacheStore.getAudioByPath(storagePath, 'user-audio')
  }

  // Get cached cover URL (uses imageCache store for blob caching)
  const getCoverUrl = async (coverStoragePath: string | null): Promise<string | undefined> => {
    if (!coverStoragePath) return undefined
    const imageCacheStore = useImageCacheStore()
    return await imageCacheStore.getImageByPath(coverStoragePath, 'user-audio')
  }

  // Actions
  const fetchMusicNodes = async (): Promise<{ success: boolean; error?: string }> => {
    const authStore = useAuthStore()
    
    if (!authStore.user) {
      return { success: false, error: 'User not authenticated' }
    }

    isLoading.value = true
    error.value = null

    try {
      const provider = getDataProvider()
      const data = await provider.query<MusicNode>('music_nodes', {
        orderBy: [{ column: 'name', ascending: true }]
      })

      // Store nodes immediately without fetching URLs
      // URLs are fetched lazily when audio is actually needed for playback
      const newMusicNodes = new Map<string, MusicNode>()
      data.forEach(node => {
        newMusicNodes.set(node.id, {
          ...node,
          url: undefined, // Lazy load on demand
          coverUrl: undefined // Lazy load on demand
        })
      })
      musicNodes.value = newMusicNodes
      
      // Preload cover images in background (non-blocking) for graph display
      const coverPaths = data
        .filter(node => node.cover_storage_path)
        .map(node => node.cover_storage_path!)
      if (coverPaths.length > 0) {
        const imageCacheStore = useImageCacheStore()
        imageCacheStore.preloadImagesByPath(coverPaths, 'user-audio').catch(err => {
          logger.warn('[MusicNodes] Cover preload failed:', err)
        })
      }

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      error.value = errorMessage
      return { success: false, error: errorMessage }
    } finally {
      isLoading.value = false
    }
  }

  const createMusicNode = async (data: CreateMusicNodeData): Promise<{ success: boolean; error?: string; data?: MusicNode }> => {
    const authStore = useAuthStore()
    
    if (!authStore.user) {
      return { success: false, error: 'User not authenticated' }
    }

    // Generate a temporary ID for optimistic update
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substring(2)}`
    const nodeName = data.name || data.file.name.replace(/\.[^/.]+$/, '')
    
    // Create temporary node for optimistic UI
    const tempNode: MusicNode = {
      id: tempId,
      user_id: authStore.user.id,
      name: nodeName,
      storage_path: '',
      file_name: data.file.name,
      file_size: data.file.size,
      duration: null,
      volume: 1.0,
      loop: false,
      playback_target: null,
      position_x: data.position_x || 0,
      position_y: data.position_y || 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      container_id: null,
      playlist_id: null,
      playlist_order: 0,
      cover_storage_path: null,
      isUploading: true
    }
    
    // Add temp node immediately for optimistic UI
    musicNodes.value.set(tempId, tempNode)
    // Trigger reactivity
    musicNodes.value = new Map(musicNodes.value)
    
    logger.info(`⏳ Creating optimistic music node: ${nodeName}`)

    try {
      const provider = getDataProvider()
      
      // Generate unique filename
      const fileExt = data.file.name.split('.').pop()
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`
      const storagePath = `${authStore.user.id}/${fileName}`

      // Upload file to storage
      const uploadResult = await provider.uploadFile('user-audio', storagePath, data.file)

      if (!uploadResult.success) {
        // Remove temp node on error
        musicNodes.value.delete(tempId)
        musicNodes.value = new Map(musicNodes.value)
        return { success: false, error: uploadResult.error }
      }

      // Get audio duration if possible (browser API)
      let duration: number | null = null
      try {
        duration = await getAudioDuration(data.file)
      } catch (e) {
        logger.warn('Could not get audio duration:', e)
      }

      // Extract and upload cover art if available
      let coverStoragePath: string | null = null
      try {
        const metadata = await musicMetadata.parseBlob(data.file)
        const picture = metadata.common.picture?.[0]
        if (picture) {
          const coverFileName = `${Date.now()}-${Math.random().toString(36).substring(2)}-cover.${picture.format.split('/')[1] || 'jpg'}`
          const coverPath = `${authStore.user.id}/covers/${coverFileName}`
          
          // Convert Uint8Array to Blob (handling potential SharedArrayBuffer types)
          const coverBlob = new Blob([new Uint8Array(picture.data)], { type: picture.format })
          const coverResult = await provider.uploadFile('user-audio', coverPath, coverBlob)
          
          if (coverResult.success) {
            coverStoragePath = coverPath
            logger.info('🎨 Extracted and uploaded cover art')
          } else {
            logger.warn('Failed to upload cover art:', coverResult.error)
          }
        }
      } catch (e) {
        logger.warn('Could not extract cover art:', e)
      }

      // Create database record
      const createData = {
        user_id: authStore.user.id,
        name: nodeName,
        storage_path: storagePath,
        file_name: data.file.name,
        file_size: data.file.size,
        duration,
        cover_storage_path: coverStoragePath,
        volume: 1.0,
        position_x: data.position_x || 0,
        position_y: data.position_y || 0
      }

      const result = await provider.insert<MusicNode>('music_nodes', createData)

      if (!result.success || !result.data) {
        // Clean up uploaded files on error
        await provider.deleteFile('user-audio', storagePath)
        if (coverStoragePath) {
          await provider.deleteFile('user-audio', coverStoragePath)
        }
        // Remove temp node on error
        musicNodes.value.delete(tempId)
        musicNodes.value = new Map(musicNodes.value)
        return { success: false, error: result.error }
      }

      // Get URLs and add real node to store (replacing temp node)
      const url = await getAudioUrl(storagePath)
      const coverUrl = await getCoverUrl(coverStoragePath)
      const nodeWithUrl = { ...result.data, url, coverUrl, isUploading: false }
      
      // Remove temp node and add real node
      musicNodes.value.delete(tempId)
      musicNodes.value.set(result.data.id, nodeWithUrl)
      musicNodes.value = new Map(musicNodes.value)
      
      logger.info(`✅ Music node created successfully: ${nodeName}`)

      return { success: true, data: nodeWithUrl }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      // Remove temp node on error
      musicNodes.value.delete(tempId)
      musicNodes.value = new Map(musicNodes.value)
      return { success: false, error: errorMessage }
    }
  }

  const updateMusicNode = async (id: string, data: UpdateMusicNodeData): Promise<{ success: boolean; error?: string; data?: MusicNode }> => {
    const authStore = useAuthStore()
    
    if (!authStore.user) {
      return { success: false, error: 'User not authenticated' }
    }

    loadingStates.value.set(id, true)

    try {
      const provider = getDataProvider()
      const result = await provider.update<MusicNode>('music_nodes', id, data)

      if (!result.success || !result.data) {
        return { success: false, error: result.error }
      }

      // Preserve URL from existing node
      const existingNode = musicNodes.value.get(id)
      const nodeWithUrl = { ...result.data, url: existingNode?.url }
      musicNodes.value.set(id, nodeWithUrl)

      return { success: true, data: nodeWithUrl }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      return { success: false, error: errorMessage }
    } finally {
      loadingStates.value.set(id, false)
    }
  }

  const deleteMusicNode = async (id: string): Promise<{ success: boolean; error?: string }> => {
    const authStore = useAuthStore()
    
    if (!authStore.user) {
      return { success: false, error: 'User not authenticated' }
    }

    loadingStates.value.set(id, true)

    try {
      const provider = getDataProvider()
      const node = musicNodes.value.get(id)
      
      // Soft-delete from database (storage files kept for potential restore)
      const result = await provider.delete('music_nodes', id)

      if (!result.success) {
        return { success: false, error: result.error }
      }

      // Clear audio cache for this node
      if (node?.storage_path) {
        const audioCacheStore = useAudioCacheStore()
        await audioCacheStore.clearAudio(node.storage_path)
      }
      if (node?.cover_storage_path) {
        const imageCacheStore = useImageCacheStore()
        // Convert storage path to cache key format before clearing
        const cacheKey = imageCacheStore.storagPathToCacheKey(node.cover_storage_path)
        await imageCacheStore.clearImage(cacheKey)
      }

      // Update store
      musicNodes.value.delete(id)
      loadingStates.value.delete(id)

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      return { success: false, error: errorMessage }
    } finally {
      loadingStates.value.set(id, false)
    }
  }

  // Create music node from existing library file (already uploaded)
  const createMusicNodeFromLibrary = async (data: {
    name: string
    storage_path: string
    file_name?: string
    file_size?: number
    duration?: number
    cover_storage_path?: string | null
    position_x?: number
    position_y?: number
  }): Promise<{ success: boolean; error?: string; data?: MusicNode }> => {
    const authStore = useAuthStore()
    
    if (!authStore.user) {
      return { success: false, error: 'User not authenticated' }
    }

    isLoading.value = true

    try {
      const provider = getDataProvider()
      
      // Create database record using existing storage path
      const createData = {
        user_id: authStore.user.id,
        name: data.name,
        storage_path: data.storage_path,
        file_name: data.file_name || data.name,
        file_size: data.file_size || null,
        duration: data.duration || null,
        cover_storage_path: data.cover_storage_path || null,
        volume: 1.0,
        position_x: data.position_x || 0,
        position_y: data.position_y || 0
      }

      const result = await provider.insert<MusicNode>('music_nodes', createData)

      if (!result.success || !result.data) {
        return { success: false, error: result.error }
      }

      // Get URLs and add to store
      const url = await getAudioUrl(data.storage_path)
      const coverUrl = await getCoverUrl(data.cover_storage_path || null)
      const nodeWithUrl = { ...result.data, url, coverUrl }
      musicNodes.value.set(result.data.id, nodeWithUrl)

      logger.info('🎵 Created music node from library:', data.name)
      return { success: true, data: nodeWithUrl }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      return { success: false, error: errorMessage }
    } finally {
      isLoading.value = false
    }
  }

  // Duplicate an existing music node (references same storage file)
  const duplicateMusicNode = async (id: string): Promise<{ success: boolean; error?: string; data?: MusicNode }> => {
    const authStore = useAuthStore()
    
    if (!authStore.user) {
      return { success: false, error: 'User not authenticated' }
    }

    const original = musicNodes.value.get(id)
    if (!original) {
      return { success: false, error: 'Music node not found' }
    }

    isLoading.value = true

    try {
      const provider = getDataProvider()
      
      const createData = {
        user_id: authStore.user.id,
        name: `${original.name} (Copy)`,
        storage_path: original.storage_path,
        file_name: original.file_name,
        file_size: original.file_size,
        duration: original.duration,
        volume: original.volume,
        loop: original.loop,
        playback_target: original.playback_target,
        position_x: original.position_x + 50,
        position_y: original.position_y + 50,
        container_id: original.container_id
      }

      const result = await provider.insert<MusicNode>('music_nodes', createData)

      if (!result.success || !result.data) {
        return { success: false, error: result.error }
      }

      // Use cached URL from original
      const url = original.url || await getAudioUrl(original.storage_path)
      const nodeWithUrl = { ...result.data, url }
      musicNodes.value.set(result.data.id, nodeWithUrl)

      logger.info('🎵 Duplicated music node:', original.name)
      return { success: true, data: nodeWithUrl }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      return { success: false, error: errorMessage }
    } finally {
      isLoading.value = false
    }
  }

  // Reorder tracks within a playlist
  const reorderPlaylistTracks = async (
    playlistId: string, 
    orderedNodeIds: string[]
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const provider = getDataProvider()
      
      // Update each node's playlist_order
      const updates = orderedNodeIds.map((nodeId, index) => ({
        id: nodeId,
        playlist_order: index
      }))

      // Batch update all nodes
      for (const update of updates) {
        const result = await provider.update<MusicNode>('music_nodes', update.id, { playlist_order: update.playlist_order })

        if (!result.success) {
          return { success: false, error: result.error }
        }

        // Update local state
        const node = musicNodes.value.get(update.id)
        if (node) {
          musicNodes.value.set(update.id, { ...node, playlist_order: update.playlist_order })
        }
      }

      logger.info('🎵 Reordered playlist tracks:', playlistId, orderedNodeIds)
      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      return { success: false, error: errorMessage }
    }
  }

  // Get next playlist order for a new track
  const getNextPlaylistOrder = (playlistId: string): number => {
    const tracks = getMusicNodesByPlaylistId.value(playlistId)
    if (tracks.length === 0) return 0
    return Math.max(...tracks.map(t => t.playlist_order)) + 1
  }

  // Helper to get audio duration from file
  const getAudioDuration = (file: File): Promise<number> => {
    return new Promise((resolve, reject) => {
      const audio = new Audio()
      audio.preload = 'metadata'
      
      audio.onloadedmetadata = () => {
        URL.revokeObjectURL(audio.src)
        resolve(audio.duration)
      }
      
      audio.onerror = () => {
        URL.revokeObjectURL(audio.src)
        reject(new Error('Could not load audio metadata'))
      }
      
      audio.src = URL.createObjectURL(file)
    })
  }

  return {
    // State
    musicNodes: computed(() => musicNodes.value),
    error: computed(() => error.value),
    isLoading: computed(() => isLoading.value),

    // Getters
    allMusicNodes,
    getMusicNodeById,
    getMusicNodesByPlaylistId,
    isMusicNodeLoading,
    getNextPlaylistOrder,

    // Utilities
    getAudioUrl,
    getCoverUrl,

    // Actions
    fetchMusicNodes,
    createMusicNode,
    createMusicNodeFromLibrary,
    updateMusicNode,
    deleteMusicNode,
    duplicateMusicNode,
    reorderPlaylistTracks
  }
})
